import { useState, useEffect, useCallback, useMemo, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { api, useTenant } from "../App";
import { getApiPrefix } from "../utils/apiHelper";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { SearchableSelect } from "../components/ui/searchable-select";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Progress } from "../components/ui/progress";
import { Switch } from "../components/ui/switch";
import { handleApiError, showSuccess } from "../utils/errorHandler";
import { PageLoader } from "../components/ui/page-loader";
import { 
  Loader2, Plus, Search, Filter, LayoutGrid, List, 
  FolderPlus, Calendar, Clock, AlertTriangle, CheckCircle2,
  ChevronRight, Users, MessageSquare, MoreVertical,
  Repeat, Trash2, Edit, Eye, ArrowRight, Pause, Play, Archive, ArchiveRestore,
  CalendarDays, ClipboardList, Circle
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import CalendarView from "../components/CalendarView";
import { useTranslation } from "../contexts/I18nContext";

export default function ProjectsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user: currentUser, tenant: tenantSlug } = useTenant();
  const [projects, setProjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState({});
  const [myAssignments, setMyAssignments] = useState([]);
  const [mainView, setMainView] = useState("tasks"); // tasks | calendar
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("kanban"); // kanban | list | archived
  const [filters, setFilters] = useState({
    categoria_id: "",
    stato: "",
    priorita: "",
    search: "",
    solo_miei: false,
    mostra_archiviati: false
  });

  // Dialogs
  const [projectDialog, setProjectDialog] = useState({ open: false, project: null });
  const [categoryDialog, setCategoryDialog] = useState({ open: false, category: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, item: null, type: null });

  // Project form
  const [projectForm, setProjectForm] = useState({
    titolo: "",
    descrizione: "",
    categoria_id: "",
    priorita: "media",
    stato: "da_fare",
    data_scadenza: "",
    is_ricorrente: false,
    tipo_ricorrenza: "settimanale",
    intervallo_ricorrenza: 1,
    giorno_settimana: null,
    giorno_mese: null,
    fine_ricorrenza: ""
  });

  // Category form
  const [categoryForm, setCategoryForm] = useState({
    nome: "",
    descrizione: "",
    colore: "#6366f1",
    icona: "folder",
    parent_id: ""
  });

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filters.categoria_id && filters.categoria_id !== "all") params.append("categoria_id", filters.categoria_id);
      if (filters.stato && filters.stato !== "all") params.append("stato", filters.stato);
      if (filters.priorita && filters.priorita !== "all") params.append("priorita", filters.priorita);
      if (filters.search) params.append("search", filters.search);
      if (filters.solo_miei) params.append("solo_miei", "true");
      if (filters.mostra_archiviati) params.append("solo_archiviati", "true");

      const [projectsRes, categoriesRes, statsRes, usersRes, assignmentsRes] = await Promise.all([
        api.get(`${getApiPrefix()}/projects?${params.toString()}`),
        api.get(`${getApiPrefix()}/project-categories`),
        api.get(`${getApiPrefix()}/projects/stats`),
        api.get(`${getApiPrefix()}/users-list`),
        api.get(`${getApiPrefix()}/my-assignments`)
      ]);

      setProjects(projectsRes.data);
      setCategories(categoriesRes.data);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setMyAssignments(assignmentsRes.data);
    } catch (err) {
      handleApiError(err, t("errors.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [filters, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Group projects by status for Kanban view
  const kanbanColumns = useMemo(() => ({
    da_fare: projects.filter(p => p.stato === "da_fare"),
    in_corso: projects.filter(p => p.stato === "in_corso"),
    completato: projects.filter(p => p.stato === "completato"),
    sospeso: projects.filter(p => p.stato === "sospeso")
  }), [projects]);

  const priorityColors = {
    alta: "bg-red-100 text-red-700 border-red-200",
    media: "bg-yellow-100 text-yellow-700 border-yellow-200",
    bassa: "bg-green-100 text-green-700 border-green-200"
  };

  const statusLabels = {
    da_fare: { label: "Da Fare", icon: Clock, color: "text-slate-500" },
    in_corso: { label: "In Corso", icon: Play, color: "text-blue-500" },
    completato: { label: "Completato", icon: CheckCircle2, color: "text-green-500" },
    sospeso: { label: "Sospeso", icon: Pause, color: "text-orange-500" }
  };

  const recurrenceLabels = {
    giornaliero: "Giornaliero",
    settimanale: "Settimanale",
    mensile: "Mensile",
    annuale: "Annuale"
  };

  const giornoSettimanaLabels = ["Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato", "Domenica"];

  const openProjectDialog = (project = null) => {
    if (project) {
      setProjectForm({
        titolo: project.titolo || "",
        descrizione: project.descrizione || "",
        categoria_id: project.categoria_id || "",
        priorita: project.priorita || "media",
        stato: project.stato || "da_fare",
        data_scadenza: project.data_scadenza || "",
        is_ricorrente: project.is_ricorrente || false,
        tipo_ricorrenza: project.tipo_ricorrenza || "settimanale",
        intervallo_ricorrenza: project.intervallo_ricorrenza || 1,
        giorno_settimana: project.giorno_settimana,
        giorno_mese: project.giorno_mese,
        fine_ricorrenza: project.fine_ricorrenza || ""
      });
    } else {
      setProjectForm({
        titolo: "",
        descrizione: "",
        categoria_id: "",
        priorita: "media",
        stato: "da_fare",
        data_scadenza: "",
        is_ricorrente: false,
        tipo_ricorrenza: "settimanale",
        intervallo_ricorrenza: 1,
        giorno_settimana: null,
        giorno_mese: null,
        fine_ricorrenza: ""
      });
    }
    setProjectDialog({ open: true, project });
  };

  const openCategoryDialog = (category = null) => {
    if (category) {
      setCategoryForm({
        nome: category.nome || "",
        descrizione: category.descrizione || "",
        colore: category.colore || "#6366f1",
        icona: category.icona || "folder",
        parent_id: category.parent_id || ""
      });
    } else {
      setCategoryForm({
        nome: "",
        descrizione: "",
        colore: "#6366f1",
        icona: "folder",
        parent_id: ""
      });
    }
    setCategoryDialog({ open: true, category });
  };

  const handleSaveProject = async () => {
    if (!projectForm.titolo.trim()) {
      handleApiError({ message: t("errors.requiredTitle") });
      return;
    }

    try {
      const payload = { ...projectForm };
      if (!payload.categoria_id || payload.categoria_id === "none") payload.categoria_id = null;
      if (!payload.data_scadenza) payload.data_scadenza = null;
      if (!payload.fine_ricorrenza) payload.fine_ricorrenza = null;

      if (projectDialog.project) {
        await api.put(`${getApiPrefix()}/projects/${projectDialog.project.id}`, payload);
        showSuccess(t("success.activityUpdated"));
      } else {
        await api.post(`${getApiPrefix()}/projects`, payload);
        showSuccess(t("success.activityCreated"));
      }
      setProjectDialog({ open: false, project: null });
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.saveFailed"));
    }
  };

  const handleSaveCategory = async () => {
    if (!categoryForm.nome.trim()) {
      handleApiError({ message: t("errors.requiredName") });
      return;
    }

    try {
      const payload = { ...categoryForm };
      if (!payload.parent_id || payload.parent_id === "none") payload.parent_id = null;

      if (categoryDialog.category) {
        await api.put(`${getApiPrefix()}/project-categories/${categoryDialog.category.id}`, payload);
        showSuccess(t("success.categoryUpdated"));
      } else {
        await api.post(`${getApiPrefix()}/project-categories`, payload);
        showSuccess(t("success.categoryCreated"));
      }
      setCategoryDialog({ open: false, category: null });
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.saveFailed"));
    }
  };

  const handleDelete = async () => {
    try {
      if (deleteDialog.type === "project") {
        await api.delete(`${getApiPrefix()}/projects/${deleteDialog.item.id}`);
        showSuccess(t("success.activityDeleted"));
      } else if (deleteDialog.type === "category") {
        await api.delete(`${getApiPrefix()}/project-categories/${deleteDialog.item.id}`);
        showSuccess(t("success.categoryDeleted"));
      }
      setDeleteDialog({ open: false, item: null, type: null });
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.deleteFailed"));
    }
  };

  const handleStatusChange = async (projectId, newStatus) => {
    try {
      await api.put(`${getApiPrefix()}/projects/${projectId}`, { stato: newStatus });
      showSuccess(t("success.statusUpdated"));
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.errorUpdateStatus"));
    }
  };

  const handleArchiveToggle = async (project) => {
    try {
      await api.put(`${getApiPrefix()}/projects/${project.id}`, { archiviato: !project.archiviato });
      showSuccess(project.archiviato ? "Attività ripristinata" : "Attività archiviata");
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.errorArchive"));
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("it-IT");
  };

  const isOverdue = (dateStr) => {
    if (!dateStr) return false;
    return new Date(dateStr) < new Date();
  };

  // Project Card Component
  const ProjectCard = ({ project, compact = false }) => (
    <Card 
      className={`group cursor-pointer transition-all hover:shadow-lg border-l-4 ${
        project.archiviato ? "border-l-gray-400 opacity-70" :
        project.priorita === "alta" ? "border-l-red-500" : 
        project.priorita === "media" ? "border-l-yellow-500" : "border-l-green-500"
      }`}
      data-testid={`project-card-${project.id}`}
    >
      <CardContent className={compact ? "p-3" : "p-4"}>
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              {project.archiviato && (
                <Archive className="h-3 w-3 text-gray-500 flex-shrink-0" />
              )}
              {project.is_ricorrente && (
                <Repeat className="h-3 w-3 text-purple-500 flex-shrink-0" />
              )}
              <h3 className="font-semibold truncate text-sm">{project.titolo}</h3>
            </div>
            {!compact && project.descrizione && (
              <p className="text-xs text-muted-foreground line-clamp-2 mb-2">{project.descrizione}</p>
            )}
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-9 w-9 sm:h-6 sm:w-6 sm:opacity-0 sm:group-hover:opacity-100">
                <MoreVertical className="h-4 w-4 sm:h-3 sm:w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => navigate(`/ns-company/progetti/${project.id}`)}>
                <Eye className="h-4 w-4 mr-2" />{t('common.view')}</DropdownMenuItem>
              <DropdownMenuItem onClick={() => openProjectDialog(project)}>
                <Edit className="h-4 w-4 mr-2" />{t('common.edit')}</DropdownMenuItem>
              <DropdownMenuSeparator />
              {!project.archiviato && project.stato !== "completato" && (
                <DropdownMenuItem onClick={() => handleStatusChange(project.id, "completato")}>
                  <CheckCircle2 className="h-4 w-4 mr-2" />{t('common.complete')}</DropdownMenuItem>
              )}
              {!project.archiviato && project.stato !== "sospeso" && project.stato !== "completato" && (
                <DropdownMenuItem onClick={() => handleStatusChange(project.id, "sospeso")}>
                  <Pause className="h-4 w-4 mr-2" /> Sospendi
                </DropdownMenuItem>
              )}
              {!project.archiviato && project.stato === "sospeso" && (
                <DropdownMenuItem onClick={() => handleStatusChange(project.id, "in_corso")}>
                  <Play className="h-4 w-4 mr-2" /> Riprendi
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => handleArchiveToggle(project)}>
                {project.archiviato ? (
                  <><ArchiveRestore className="h-4 w-4 mr-2" />{t('common.reset')}</>
                ) : (
                  <><Archive className="h-4 w-4 mr-2" />{t('common.archive')}</>
                )}
              </DropdownMenuItem>
              <DropdownMenuItem 
                className="text-red-600" 
                onClick={() => setDeleteDialog({ open: true, item: project, type: "project" })}
              >
                <Trash2 className="h-4 w-4 mr-2" />{t('common.delete')}</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Progress */}
        <div className="mt-2">
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="text-muted-foreground">{project.completed_steps || 0}/{project.total_steps || 0} step</span>
            <span className="font-medium">{project.progress || 0}%</span>
          </div>
          <Progress value={project.progress || 0} className="h-1.5" />
        </div>

        {/* Footer info */}
        <div className="flex items-center justify-between mt-3 text-xs">
          <div className="flex items-center gap-2">
            {project.categoria_nome && (
              <Badge variant="outline" className="text-xs px-1.5 py-0" style={{ borderColor: project.categoria_colore }}>
                {project.categoria_nome}
              </Badge>
            )}
            <Badge className={`text-xs px-1.5 py-0 ${priorityColors[project.priorita]}`}>
              {project.priorita.charAt(0).toUpperCase() + project.priorita.slice(1)}
            </Badge>
          </div>
          <div className="flex items-center gap-2 text-muted-foreground">
            {project.participants_count > 0 && (
              <span className="flex items-center gap-0.5">
                <Users className="h-3 w-3" /> {project.participants_count}
              </span>
            )}
            {project.data_scadenza && (
              <span className={`flex items-center gap-0.5 ${isOverdue(project.data_scadenza) && project.stato !== "completato" ? "text-red-500" : ""}`}>
                <Calendar className="h-3 w-3" /> {formatDate(project.data_scadenza)}
              </span>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  // Kanban Column Component
  const KanbanColumn = ({ status, projects }) => {
    const StatusIcon = statusLabels[status].icon;
    return (
      <div className="flex-shrink-0 w-[85vw] sm:w-auto sm:flex-1 sm:min-w-[280px] sm:max-w-[350px]">
        <div className={`flex items-center gap-2 mb-3 ${statusLabels[status].color}`}>
          <StatusIcon className="h-4 w-4" />
          <h3 className="font-semibold text-sm sm:text-base">{statusLabels[status].label}</h3>
          <Badge variant="secondary" className="ml-auto">{projects.length}</Badge>
        </div>
        <div className="space-y-3 max-h-[calc(100vh-350px)] overflow-y-auto pr-2">
          {projects.map(project => (
            <div key={project.id} onClick={() => navigate(`/${tenantSlug}/progetti/${project.id}`)}>
              <ProjectCard project={project} compact />
            </div>
          ))}
          {projects.length === 0 && (
            <div className="text-center py-8 text-muted-foreground text-sm border-2 border-dashed rounded-lg">
              Nessun progetto
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return <PageLoader variant="section" text={t("common.loading")} />;
  }

  return (
    <div className="space-y-6" data-testid="projects-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold truncate">{t("nav.toDo")}</h1>
          <p className="text-xs sm:text-sm text-muted-foreground">Organizza attività e collabora con il team</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Main View Toggle */}
          <div className="flex items-center border rounded-lg p-1 bg-muted/30">
            <Button
              variant={mainView === "tasks" ? "secondary" : "ghost"}
              size="sm"
              onClick={() => setMainView("tasks")}
              className="gap-2 h-9 sm:h-8 px-3"
            >
              <LayoutGrid className="h-5 w-5 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Attività</span>
            </Button>
            <Button
              variant={mainView === "calendar" ? "secondary" : "ghost"}
              size="sm"
              onClick={() => setMainView("calendar")}
              className="gap-2 h-9 sm:h-8 px-3"
            >
              <CalendarDays className="h-5 w-5 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Calendario</span>
            </Button>
          </div>
          {mainView === "tasks" && (
            <>
              <Button variant="outline" size="sm" onClick={() => openCategoryDialog()} className="h-10 sm:h-9 px-3">
                <FolderPlus className="h-5 w-5 sm:h-4 sm:w-4 sm:mr-2" />
                <span className="hidden sm:inline">{t('common.category')}</span>
              </Button>
              <Button size="sm" onClick={() => openProjectDialog()} data-testid="create-project-btn" className="h-10 sm:h-9 px-3">
                <Plus className="h-5 w-5 sm:h-4 sm:w-4 sm:mr-2" />
                <span className="hidden sm:inline">Nuova Attività</span>
                <span className="sm:hidden ml-1.5">Nuova</span>
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Calendar View */}
      {mainView === "calendar" ? (
        <CalendarView />
      ) : (
        <>
          {/* Stats Cards */}
          <div className="grid gap-2 sm:gap-4 grid-cols-2 sm:grid-cols-3 md:grid-cols-6">
            <Card className="bg-gradient-to-br from-slate-50 to-slate-100">
              <CardContent className="p-3 sm:pt-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-slate-200">
                    <Clock className="h-5 w-5 text-slate-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wide truncate">{t('common.toDo')}</p>
                    <p className="text-xl sm:text-2xl font-bold">{stats.da_fare || 0}</p>
                  </div>
                </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100">
          <CardContent className="p-3 sm:pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-200">
                <Play className="h-5 w-5 text-blue-600" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wide truncate">{t('common.inProgress')}</p>
                <p className="text-xl sm:text-2xl font-bold">{stats.in_corso || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-50 to-green-100">
          <CardContent className="p-3 sm:pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-200">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wide truncate">{t('common.completed')}</p>
                <p className="text-xl sm:text-2xl font-bold">{stats.completato || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-red-50 to-red-100">
          <CardContent className="p-3 sm:pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-200">
                <AlertTriangle className="h-5 w-5 text-red-600" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wide truncate">{t('common.expiringSoon')}</p>
                <p className="text-xl sm:text-2xl font-bold">{stats.in_scadenza || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100">
          <CardContent className="p-3 sm:pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-200">
                <Users className="h-5 w-5 text-purple-600" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wide truncate">Mie Attività</p>
                <p className="text-xl sm:text-2xl font-bold">{stats.mie_attivita || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card 
          className={`bg-gradient-to-br cursor-pointer transition-all ${filters.mostra_archiviati ? "from-gray-200 to-gray-300 ring-2 ring-gray-400" : "from-gray-50 to-gray-100 hover:from-gray-100 hover:to-gray-200"}`}
          onClick={() => setFilters({ ...filters, mostra_archiviati: !filters.mostra_archiviati })}
        >
          <CardContent className="p-3 sm:pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-gray-200">
                <Archive className="h-5 w-5 text-gray-600" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wide truncate">Archiviate</p>
                <p className="text-xl sm:text-2xl font-bold">{stats.archiviati || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and View Toggle */}
      <Card>
        <CardContent className="p-3 sm:pt-4">
          <div className="flex flex-col lg:flex-row gap-3 sm:gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Cerca attività..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="pl-9"
                data-testid="search-input"
              />
            </div>
            <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2">
              <Select value={filters.categoria_id} onValueChange={(v) => setFilters({ ...filters, categoria_id: v })}>
                <SelectTrigger className="w-full sm:w-[180px]">
                  <SelectValue placeholder={t("common.category")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('common.all')}</SelectItem>
                  {(() => {
                    // Mostra categorie in gerarchia: parent + figli indentati sotto ogni parent
                    const parents = categories.filter(c => !c.parent_id);
                    const childrenByParent = categories.reduce((acc, c) => {
                      if (c.parent_id) {
                        (acc[c.parent_id] = acc[c.parent_id] || []).push(c);
                      }
                      return acc;
                    }, {});
                    const rows = [];
                    parents.forEach(p => {
                      rows.push(
                        <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>
                      );
                      (childrenByParent[p.id] || []).forEach(child => {
                        rows.push(
                          <SelectItem key={child.id} value={child.id}>
                            <span className="text-slate-500 mr-1">└</span>{child.nome}
                          </SelectItem>
                        );
                      });
                    });
                    // Orphan children (parent_id valorizzato ma parent non trovato): mostrali comunque
                    const orphans = categories.filter(c => c.parent_id && !parents.find(p => p.id === c.parent_id));
                    orphans.forEach(o => rows.push(<SelectItem key={o.id} value={o.id}>{o.nome}</SelectItem>));
                    return rows;
                  })()}
                </SelectContent>
              </Select>
              <Select value={filters.stato} onValueChange={(v) => setFilters({ ...filters, stato: v })}>
                <SelectTrigger className="w-full sm:w-[130px]">
                  <SelectValue placeholder={t("common.status")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('common.all')}</SelectItem>
                  <SelectItem value="da_fare">{t('common.toDo')}</SelectItem>
                  <SelectItem value="in_corso">{t('common.inProgress')}</SelectItem>
                  <SelectItem value="completato">{t('common.completed')}</SelectItem>
                  <SelectItem value="sospeso">{t('common.suspended')}</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filters.priorita} onValueChange={(v) => setFilters({ ...filters, priorita: v })}>
                <SelectTrigger className="w-full sm:w-[120px]">
                  <SelectValue placeholder={t("common.priority")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('common.all')}</SelectItem>
                  <SelectItem value="alta">Alta</SelectItem>
                  <SelectItem value="media">Media</SelectItem>
                  <SelectItem value="bassa">Bassa</SelectItem>
                </SelectContent>
              </Select>
              <div className="flex items-center justify-center gap-2 px-3 border rounded-md h-9 sm:h-10">
                <Switch
                  id="solo-miei"
                  checked={filters.solo_miei}
                  onCheckedChange={(v) => setFilters({ ...filters, solo_miei: v })}
                />
                <Label htmlFor="solo-miei" className="text-sm whitespace-nowrap">Solo miei</Label>
              </div>
              <div className="flex items-center justify-center border rounded-md">
                <Button
                  variant={viewMode === "kanban" ? "secondary" : "ghost"}
                  size="sm"
                  onClick={() => setViewMode("kanban")}
                  className="rounded-r-none h-10 sm:h-9 w-11 sm:w-auto px-3"
                >
                  <LayoutGrid className="h-5 w-5 sm:h-4 sm:w-4" />
                </Button>
                <Button
                  variant={viewMode === "list" ? "secondary" : "ghost"}
                  size="sm"
                  onClick={() => setViewMode("list")}
                  className="rounded-l-none h-10 sm:h-9 w-11 sm:w-auto px-3"
                >
                  <List className="h-5 w-5 sm:h-4 sm:w-4" />
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Content */}
      {viewMode === "kanban" ? (
        <div className="flex gap-4 overflow-x-auto pb-4">
          <KanbanColumn status="da_fare" projects={kanbanColumns.da_fare} />
          <KanbanColumn status="in_corso" projects={kanbanColumns.in_corso} />
          <KanbanColumn status="completato" projects={kanbanColumns.completato} />
          <KanbanColumn status="sospeso" projects={kanbanColumns.sospeso} />
        </div>
      ) : (
        <div className="grid gap-3 sm:gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {projects.map(project => (
            <div key={project.id} onClick={() => navigate(`/${tenantSlug}/progetti/${project.id}`)}>
              <ProjectCard project={project} />
            </div>
          ))}
          {projects.length === 0 && (
            <Card className="col-span-full">
              <CardContent className="text-center py-12">
                <p className="text-muted-foreground">
                  {filters.mostra_archiviati ? "Nessuna attività archiviata" : "Nessuna attività trovata"}
                </p>
                {!filters.mostra_archiviati && (
                  <Button variant="outline" className="mt-4" onClick={() => openProjectDialog()}>
                    <Plus className="h-4 w-4 mr-2" /> Crea la prima attività
                  </Button>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* I tuoi incarichi - My Assignments Section */}
      {myAssignments.length > 0 && (
        <Card className="border-indigo-200">
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="text-base sm:text-lg flex items-center gap-2 text-indigo-700">
              <ClipboardList className="h-5 w-5" />
              I tuoi incarichi ({myAssignments.filter(a => !a.completato).length} da completare)
            </CardTitle>
            <CardDescription className="text-xs sm:text-sm">Step assegnati a te nelle varie attività</CardDescription>
          </CardHeader>
          <CardContent className="p-4 sm:p-6 pt-0 sm:pt-0">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {myAssignments.filter(a => !a.completato).map(assignment => (
                <div
                  key={assignment.id}
                  className="flex items-start gap-3 p-3 rounded-lg border bg-white hover:shadow-md transition-all cursor-pointer"
                  onClick={() => navigate(`/${tenantSlug}/progetti/${assignment.progetto_id}`)}
                >
                  <Circle className={`h-4 w-4 mt-0.5 flex-shrink-0 ${
                    assignment.progetto_priorita === "alta" ? "text-red-400" :
                    assignment.progetto_priorita === "media" ? "text-yellow-400" : "text-green-400"
                  }`} />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{assignment.titolo}</p>
                    <p className="text-xs text-muted-foreground truncate mt-0.5">
                      {assignment.progetto_titolo}
                    </p>
                    <div className="flex items-center gap-2 mt-1.5">
                      {assignment.categoria_nome && (
                        <Badge variant="outline" className="text-xs px-1.5 py-0" style={{ borderColor: assignment.categoria_colore }}>
                          {assignment.categoria_nome}
                        </Badge>
                      )}
                      {assignment.data_scadenza && (
                        <span className={`text-xs flex items-center gap-0.5 ${
                          new Date(assignment.data_scadenza) < new Date() ? "text-red-500" : "text-muted-foreground"
                        }`}>
                          <Calendar className="h-3 w-3" />
                          {new Date(assignment.data_scadenza).toLocaleDateString("it-IT")}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {myAssignments.some(a => a.completato) && (
              <div className="mt-4 pt-3 border-t">
                <p className="text-xs text-muted-foreground mb-2">
                  {myAssignments.filter(a => a.completato).length} completati
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Categories Section with Stats - Hierarchical Table */}
      {categories.length > 0 && (() => {
        // Costruzione gerarchia parent → children con aggregazione statistiche
        const parents = categories.filter(c => !c.parent_id);
        const childrenByParent = categories.reduce((acc, c) => {
          if (c.parent_id) (acc[c.parent_id] = acc[c.parent_id] || []).push(c);
          return acc;
        }, {});
        const num = (v) => Number(v || 0);
        const sumChild = (children, field) => (children || []).reduce((s, ch) => s + num(ch[field]), 0);
        const rows = parents.map(p => {
          const children = (childrenByParent[p.id] || []).filter(ch => num(ch.attivita_count) + num(ch.archiviato_count) > 0);
          const aggregated = {
            da_fare: num(p.da_fare_count) + sumChild(childrenByParent[p.id], 'da_fare_count'),
            in_corso: num(p.in_corso_count) + sumChild(childrenByParent[p.id], 'in_corso_count'),
            completato: num(p.completato_count) + sumChild(childrenByParent[p.id], 'completato_count'),
            sospeso: num(p.sospeso_count) + sumChild(childrenByParent[p.id], 'sospeso_count'),
            archiviato: num(p.archiviato_count) + sumChild(childrenByParent[p.id], 'archiviato_count'),
            attivita: num(p.attivita_count) + sumChild(childrenByParent[p.id], 'attivita_count'),
          };
          return { parent: p, children, aggregated };
        });

        return (
          <Card data-testid="category-summary-card">
            <CardHeader className="p-4 sm:p-6">
              <CardTitle className="text-base sm:text-lg">Riepilogo per Categoria</CardTitle>
              <CardDescription className="text-xs sm:text-sm">Stato delle attività — parent con totali aggregati (sé stesso + sottocategorie). Clicca una riga per filtrare.</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-xs sm:text-sm">
                  <thead className="bg-slate-50 border-y">
                    <tr>
                      <th className="text-left px-3 sm:px-4 py-2 font-medium text-slate-600">Categoria</th>
                      <th className="text-right px-2 sm:px-3 py-2 font-medium text-slate-600">
                        <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3 text-slate-500" /><span className="hidden sm:inline">{t('common.toDo')}</span><span className="sm:hidden">TD</span></span>
                      </th>
                      <th className="text-right px-2 sm:px-3 py-2 font-medium text-slate-600">
                        <span className="inline-flex items-center gap-1"><Play className="h-3 w-3 text-blue-500" /><span className="hidden sm:inline">{t('common.inProgress')}</span><span className="sm:hidden">Corso</span></span>
                      </th>
                      <th className="text-right px-2 sm:px-3 py-2 font-medium text-slate-600">
                        <span className="inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3 text-green-500" /><span className="hidden sm:inline">{t('common.completed')}</span><span className="sm:hidden">OK</span></span>
                      </th>
                      <th className="hidden sm:table-cell text-right px-2 sm:px-3 py-2 font-medium text-slate-600">
                        <span className="inline-flex items-center gap-1"><Pause className="h-3 w-3 text-orange-500" />Sospese</span>
                      </th>
                      <th className="hidden md:table-cell text-right px-2 sm:px-3 py-2 font-medium text-slate-600">
                        <span className="inline-flex items-center gap-1"><Archive className="h-3 w-3 text-gray-500" />Archiviate</span>
                      </th>
                      <th className="text-right px-2 sm:px-3 py-2 font-medium text-slate-600"><span className="hidden sm:inline">Totale attive</span><span className="sm:hidden">Tot</span></th>
                      <th className="w-8 sm:w-10 px-1 sm:px-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(({ parent, children, aggregated }) => (
                      <Fragment key={parent.id}>
                        <tr
                          className="border-b hover:bg-slate-50 cursor-pointer"
                          style={{ borderLeftWidth: '4px', borderLeftColor: parent.colore }}
                          onClick={() => setFilters({ ...filters, categoria_id: parent.id, mostra_archiviati: false })}
                          data-testid={`category-row-${parent.id}`}
                        >
                          <td className="px-3 sm:px-4 py-2 font-semibold truncate max-w-[140px] sm:max-w-none" style={{ color: parent.colore }}>{parent.nome}</td>
                          <td className="px-2 sm:px-3 py-2 text-right font-medium">{aggregated.da_fare}</td>
                          <td className="px-2 sm:px-3 py-2 text-right font-medium">{aggregated.in_corso}</td>
                          <td className="px-2 sm:px-3 py-2 text-right font-medium">{aggregated.completato}</td>
                          <td className="hidden sm:table-cell px-2 sm:px-3 py-2 text-right font-medium">{aggregated.sospeso}</td>
                          <td className="hidden md:table-cell px-2 sm:px-3 py-2 text-right font-medium text-gray-500">{aggregated.archiviato}</td>
                          <td className="px-2 sm:px-3 py-2 text-right font-semibold">{aggregated.attivita}</td>
                          <td className="px-1 sm:px-2 py-2 text-right">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-9 w-9 sm:h-6 sm:w-6"
                              onClick={(e) => { e.stopPropagation(); openCategoryDialog(parent); }}
                              data-testid={`edit-category-${parent.id}`}
                            >
                              <Edit className="h-4 w-4 sm:h-3 sm:w-3" />
                            </Button>
                          </td>
                        </tr>
                        {children.map(ch => (
                          <tr
                            key={ch.id}
                            className="border-b hover:bg-slate-50 cursor-pointer bg-slate-50/40"
                            onClick={() => setFilters({ ...filters, categoria_id: ch.id, mostra_archiviati: false })}
                            data-testid={`subcategory-row-${ch.id}`}
                          >
                            <td className="pl-6 sm:pl-10 pr-3 sm:pr-4 py-1.5 text-slate-700 truncate max-w-[140px] sm:max-w-none">
                              <span className="text-slate-400 mr-1 sm:mr-2">└</span>
                              <span style={{ color: ch.colore }}>{ch.nome}</span>
                            </td>
                            <td className="px-2 sm:px-3 py-1.5 text-right">{num(ch.da_fare_count)}</td>
                            <td className="px-2 sm:px-3 py-1.5 text-right">{num(ch.in_corso_count)}</td>
                            <td className="px-2 sm:px-3 py-1.5 text-right">{num(ch.completato_count)}</td>
                            <td className="hidden sm:table-cell px-2 sm:px-3 py-1.5 text-right">{num(ch.sospeso_count)}</td>
                            <td className="hidden md:table-cell px-2 sm:px-3 py-1.5 text-right text-gray-500">{num(ch.archiviato_count)}</td>
                            <td className="px-2 sm:px-3 py-1.5 text-right font-medium">{num(ch.attivita_count)}</td>
                            <td className="px-1 sm:px-2 py-1.5 text-right">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-9 w-9 sm:h-6 sm:w-6"
                                onClick={(e) => { e.stopPropagation(); openCategoryDialog(ch); }}
                                data-testid={`edit-subcategory-${ch.id}`}
                              >
                                <Edit className="h-4 w-4 sm:h-3 sm:w-3" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        );
      })()}
        </>
      )}

      {/* Project Dialog */}
      <Dialog open={projectDialog.open} onOpenChange={(open) => setProjectDialog({ ...projectDialog, open })}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{projectDialog.project ? t("projects.editProject") : t("projects.newProject")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Titolo *</Label>
              <Input
                value={projectForm.titolo}
                onChange={(e) => setProjectForm({ ...projectForm, titolo: e.target.value })}
                placeholder="Nome dell'attività"
                data-testid="project-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label>{t("common.description")}</Label>
              <Textarea
                value={projectForm.descrizione}
                onChange={(e) => setProjectForm({ ...projectForm, descrizione: e.target.value })}
                placeholder="Descrizione dell'attività..."
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t("common.category")}</Label>
                <SearchableSelect
                  options={[
                    { value: "none", label: "Nessuna" },
                    ...categories.map(cat => ({ value: cat.id, label: cat.nome }))
                  ]}
                  value={projectForm.categoria_id}
                  onValueChange={(v) => setProjectForm({ ...projectForm, categoria_id: v })}
                  placeholder={t("common.selectPlaceholder")}
                  searchPlaceholder="Cerca categoria..."
                  testId="select-categoria-progetto"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('common.priority')}</Label>
                <Select value={projectForm.priorita} onValueChange={(v) => setProjectForm({ ...projectForm, priorita: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="alta">🔴 Alta</SelectItem>
                    <SelectItem value="media">🟡 Media</SelectItem>
                    <SelectItem value="bassa">🟢 Bassa</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t("common.status")}</Label>
                <Select value={projectForm.stato} onValueChange={(v) => setProjectForm({ ...projectForm, stato: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="da_fare">{t('common.toDo')}</SelectItem>
                    <SelectItem value="in_corso">{t('common.inProgress')}</SelectItem>
                    <SelectItem value="completato">{t('common.completed')}</SelectItem>
                    <SelectItem value="sospeso">{t('common.suspended')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('invoices.dueDate')}</Label>
                <Input
                  type="date"
                  value={projectForm.data_scadenza}
                  onChange={(e) => setProjectForm({ ...projectForm, data_scadenza: e.target.value })}
                />
              </div>
            </div>

            {/* Recurrence Section */}
            <div className="border rounded-lg p-4 space-y-4 bg-muted/30">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Repeat className="h-4 w-4 text-purple-500" />
                  <Label className="font-medium">Attività Ricorrente</Label>
                </div>
                <Switch
                  checked={projectForm.is_ricorrente}
                  onCheckedChange={(v) => setProjectForm({ ...projectForm, is_ricorrente: v })}
                />
              </div>

              {projectForm.is_ricorrente && (
                <div className="space-y-4 pt-2">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Tipo Ricorrenza</Label>
                      <Select 
                        value={projectForm.tipo_ricorrenza} 
                        onValueChange={(v) => setProjectForm({ ...projectForm, tipo_ricorrenza: v })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="giornaliero">{t('common.daily')}</SelectItem>
                          <SelectItem value="settimanale">{t('common.weekly')}</SelectItem>
                          <SelectItem value="mensile">{t("common.monthly")}</SelectItem>
                          <SelectItem value="annuale">{t("common.annual")}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Ogni</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          min={1}
                          value={projectForm.intervallo_ricorrenza}
                          onChange={(e) => setProjectForm({ ...projectForm, intervallo_ricorrenza: parseInt(e.target.value) || 1 })}
                          className="w-20"
                        />
                        <span className="text-sm text-muted-foreground">
                          {projectForm.tipo_ricorrenza === "giornaliero" ? "giorni" :
                           projectForm.tipo_ricorrenza === "settimanale" ? "settimane" :
                           projectForm.tipo_ricorrenza === "mensile" ? "mesi" : "anni"}
                        </span>
                      </div>
                    </div>
                  </div>

                  {projectForm.tipo_ricorrenza === "settimanale" && (
                    <div className="space-y-2">
                      <Label>Giorno della Settimana</Label>
                      <Select 
                        value={projectForm.giorno_settimana?.toString() || ""} 
                        onValueChange={(v) => setProjectForm({ ...projectForm, giorno_settimana: v ? parseInt(v) : null })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={t("common.selectPlaceholder")} />
                        </SelectTrigger>
                        <SelectContent>
                          {giornoSettimanaLabels.map((label, idx) => (
                            <SelectItem key={label} value={idx.toString()}>{label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {projectForm.tipo_ricorrenza === "mensile" && (
                    <div className="space-y-2">
                      <Label>Giorno del Mese</Label>
                      <Input
                        type="number"
                        min={1}
                        max={31}
                        value={projectForm.giorno_mese || ""}
                        onChange={(e) => setProjectForm({ ...projectForm, giorno_mese: parseInt(e.target.value) || null })}
                        placeholder="1-31"
                      />
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label>Fine Ricorrenza (opzionale)</Label>
                    <Input
                      type="date"
                      value={projectForm.fine_ricorrenza}
                      onChange={(e) => setProjectForm({ ...projectForm, fine_ricorrenza: e.target.value })}
                    />
                  </div>

                  <div className="bg-purple-50 border border-purple-200 rounded-md p-3 text-sm text-purple-700">
                    <strong>Riepilogo:</strong> Questa attività si ripeterà ogni{" "}
                    {projectForm.intervallo_ricorrenza > 1 ? `${projectForm.intervallo_ricorrenza} ` : ""}
                    {projectForm.tipo_ricorrenza === "giornaliero" ? (projectForm.intervallo_ricorrenza > 1 ? "giorni" : "giorno") :
                     projectForm.tipo_ricorrenza === "settimanale" ? (projectForm.intervallo_ricorrenza > 1 ? "settimane" : "settimana") :
                     projectForm.tipo_ricorrenza === "mensile" ? (projectForm.intervallo_ricorrenza > 1 ? "mesi" : "mese") : 
                     (projectForm.intervallo_ricorrenza > 1 ? "anni" : "anno")}
                    {projectForm.tipo_ricorrenza === "settimanale" && projectForm.giorno_settimana !== null && 
                      ` di ${giornoSettimanaLabels[projectForm.giorno_settimana]}`}
                    {projectForm.tipo_ricorrenza === "mensile" && projectForm.giorno_mese && 
                      ` il giorno ${projectForm.giorno_mese}`}
                    {projectForm.fine_ricorrenza && ` fino al ${formatDate(projectForm.fine_ricorrenza)}`}
                  </div>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProjectDialog({ open: false, project: null })}>{t('common.cancel')}</Button>
            <Button onClick={handleSaveProject} data-testid="save-project-btn">
              {projectDialog.project ? t("common.save") : "Crea Attività"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Category Dialog */}
      <Dialog open={categoryDialog.open} onOpenChange={(open) => setCategoryDialog({ ...categoryDialog, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{categoryDialog.category ? t("categories.editCategory") : t("categories.newCategory")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{t('common.name')} *</Label>
              <Input
                value={categoryForm.nome}
                onChange={(e) => setCategoryForm({ ...categoryForm, nome: e.target.value })}
                placeholder="Nome della categoria"
              />
            </div>
            <div className="space-y-2">
              <Label>{t("common.description")}</Label>
              <Textarea
                value={categoryForm.descrizione}
                onChange={(e) => setCategoryForm({ ...categoryForm, descrizione: e.target.value })}
                placeholder={t("common.descriptionPlaceholder")}
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Colore</Label>
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={categoryForm.colore}
                    onChange={(e) => setCategoryForm({ ...categoryForm, colore: e.target.value })}
                    className="w-12 h-10 p-1 cursor-pointer"
                  />
                  <Input
                    value={categoryForm.colore}
                    onChange={(e) => setCategoryForm({ ...categoryForm, colore: e.target.value })}
                    className="flex-1"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Categoria Padre</Label>
                <SearchableSelect
                  options={[
                    { value: "none", label: "Nessuna (principale)" },
                    ...categories.filter(c => !c.parent_id && c.id !== categoryDialog.category?.id).map(cat => ({ value: cat.id, label: cat.nome }))
                  ]}
                  value={categoryForm.parent_id}
                  onValueChange={(v) => setCategoryForm({ ...categoryForm, parent_id: v })}
                  placeholder={t("common.none")}
                  searchPlaceholder="Cerca categoria..."
                  testId="select-categoria-padre"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            {categoryDialog.category && (
              <Button 
                variant="destructive" 
                onClick={() => {
                  setCategoryDialog({ open: false, category: null });
                  setDeleteDialog({ open: true, item: categoryDialog.category, type: "category" });
                }}
                className="mr-auto"
              >
                <Trash2 className="h-4 w-4 mr-2" />{t('common.delete')}</Button>
            )}
            <Button variant="outline" onClick={() => setCategoryDialog({ open: false, category: null })}>{t('common.cancel')}</Button>
            <Button onClick={handleSaveCategory}>
              {categoryDialog.category ? t("common.save") : "Crea Categoria"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("confirm.confirmDeletion")}</DialogTitle>
            <DialogDescription>
              Sei sicuro di voler eliminare {deleteDialog.type === "project" ? "questa attività" : "questa categoria"}?
              {deleteDialog.type === "project" && " Tutti gli step, note e partecipanti associati verranno eliminati."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, item: null, type: null })}>{t('common.cancel')}</Button>
            <Button variant="destructive" onClick={handleDelete}>{t('common.delete')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
